'use client';

import { LDProvider as LaunchDarklyProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import { type ReactNode, useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';

interface LDProviderProps {
  children: ReactNode;
}

/**
 * LaunchDarkly Provider wrapper that automatically sets user context
 * based on the authenticated user's information.
 */
export function LDProvider({ children }: LDProviderProps) {
  const clientSideId = process.env.NEXT_PUBLIC_LD_CLIENT_SIDE_ID;

  // If no client ID is configured, render children without LaunchDarkly
  if (!clientSideId) {
    console.warn(
      '[LaunchDarkly] NEXT_PUBLIC_LD_CLIENT_SIDE_ID is not set. Feature flags disabled.'
    );
    return <>{children}</>;
  }

  return (
    <LaunchDarklyProvider
      clientSideID={clientSideId}
      context={{
        kind: 'user',
        key: 'anonymous', // Will be updated when user is authenticated
        anonymous: true,
      }}
    >
      <LDContextUpdater>{children}</LDContextUpdater>
    </LaunchDarklyProvider>
  );
}

/**
 * Internal component that updates LaunchDarkly context when user authenticates.
 */
function LDContextUpdater({ children }: { children: ReactNode }) {
  const ldClient = useLDClient();
  const { user, isAuthenticated } = useAuthStore();
  const [hasIdentified, setHasIdentified] = useState(false);

  useEffect(() => {
    if (!ldClient) return;

    // Get the first organisation ID
    const organisationId = user?.organisations?.[0]?.id || '';

    // If user is authenticated and we have user data, identify them
    if (isAuthenticated && user?.user) {
      const userContext = {
        kind: 'user' as const,
        key: user.user.email,
        email: user.user.email,
        name: `${user.user.first_name} ${user.user.last_name}`.trim(),
        firstName: user.user.first_name,
        lastName: user.user.last_name,
        userId: String(user.user.id),
        organisationId: organisationId,
      };

      ldClient
        .identify(userContext)
        .then(() => {
          setHasIdentified(true);
        })
        .catch((error) => {
          console.error('[LaunchDarkly] Failed to identify user:', error);
        });
    } else if (!isAuthenticated && hasIdentified) {
      // User logged out, reset to anonymous
      ldClient
        .identify({
          kind: 'user',
          key: 'anonymous',
          anonymous: true,
        })
        .then(() => {
          setHasIdentified(false);
          console.log('[LaunchDarkly] Reset to anonymous user');
        })
        .catch((error) => {
          console.error('[LaunchDarkly] Failed to reset to anonymous:', error);
        });
    }
  }, [ldClient, user, isAuthenticated, hasIdentified]);

  return <>{children}</>;
}

export default LDProvider;
